import { Avatar, Space, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { EditOutlined } from "@ant-design/icons";
import {
  filter_issue_statuses,
  getIssueStatusLabel,
} from "helpers/options/issue_statuses";
import EllipsisText from "components/EllipsisText";
import formatPhone from "helpers/phone_formatter";
import {
  worker_billing_status_filter_options,
  administration_billing_status_filter_options,
  getAdministratorBillingStatus,
  getWorkerBillingStatus,
} from "helpers/options/billing_statuses";
import getIssueType, {
  getIssueTypeOption,
  issue_type_options,
} from "helpers/issue_types";
import { Tooltip } from "@mui/material";

const selectTagRender = (props) => {
  const { value, closable, onClose } = props;
  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const issue_type = getIssueType(getIssueTypeOption(value));

  return (
    <Tag
      color={issue_type.color}
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginInlineEnd: 4 }}
    >
      {issue_type.label}
    </Tag>
  );
};

const getColumns = (
  workers_options = [],
  issue_kind_options = [],
  issues_index_columns = []
) => {
  const renderLink = (id, name, content) => {
    if ([issues_index_columns?.[0]].includes(name)) {
      return (
        <Link to={`/issues/${id}`}>
          {content}{" "}
          <Tooltip title="Przejdź do edycji">
            <EditOutlined />
          </Tooltip>
        </Link>
      );
    }
    return content;
  };

  return [
    {
      title: "ID",
      dataIndex: "id",
      sortable: true,
      searchable: true,
      search_method: "eq",
      width: 200,
      render: (_, { id }) => renderLink(id, "id", id),
    },
    {
      title: "Nazwa ulicy",
      dataIndex: "client_street",
      sortable: true,
      searchable: true,
      search_method: "matches",
      render: (_, { id, client_street }) =>
        renderLink(id, "client_street", client_street),
    },
    {
      title: "Numer budynku",
      dataIndex: "client_building_number",
      sortable: true,
      searchable: true,
      width: 130,
      search_method: "matches",
      render: (_, { id, client_building_number }) =>
        renderLink(id, "client_building_number", client_building_number),
    },
    {
      title: "Numer mieszkania",
      dataIndex: "client_apartment_number",
      sortable: true,
      searchable: true,
      width: 130,
      search_method: "matches",
      render: (_, { id, client_apartment_number }) =>
        renderLink(id, "client_apartment_number", client_apartment_number),
    },
    {
      title: "Telefon do właściciela / administratora",
      dataIndex: "client_phone",
      sortable: true,
      searchable: true,
      search_method: "matches",
      search_type: "number",
      width: 220,
      render: (_, { id, client_phone }) =>
        renderLink(
          id,
          "client_phone",
          <span className="text-nowrap">{formatPhone(client_phone)}</span>
        ),
    },
    {
      title: "Data umówienia",
      dataIndex: "service_date",
      sortable: true,
      searchable: true,
      search_method: "date",
      search_component: "date-picker",
      width: 250,
      render: (_, { id, service_date }) =>
        renderLink(
          id,
          "service_date",
          service_date ? (
            <span className="text-nowrap">
              {dayjs(service_date).format("DD-MM-YYYY HH:mm")}
            </span>
          ) : (
            "-"
          )
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      sortable: true,
      searchable: true,
      search_method: "in",
      search_component: "select",
      options: filter_issue_statuses,
      render: (_, { id, status }) =>
        renderLink(id, "status", getIssueStatusLabel(status)),
      width: 150,
    },
    {
      title: "Pracownik",
      dataIndex: "worker_id",
      sortable: true,
      searchable: true,
      search_method: "in",
      search_component: "select",
      options: workers_options,
      render: (_, { worker }) =>
        worker?.id ? (
          <Space className="text-nowrap">
            <Avatar style={{ backgroundColor: worker?.color }}>
              {worker.initials}
            </Avatar>
            <Typography.Text delete={worker?.is_deleted}>
              <EllipsisText text={worker.full_name} limit={30} />
            </Typography.Text>
          </Space>
        ) : (
          "-"
        ),
      width: 200,
    },
    {
      title: "Stan roz. pracownik",
      dataIndex: "worker_billing_status",
      sortable: false,
      searchable: true,
      search_method: "in",
      search_component: "select",
      options: worker_billing_status_filter_options,
      render: (_, { id, worker_billing_status }) =>
        renderLink(
          id,
          "worker_billing_status",
          getWorkerBillingStatus(worker_billing_status)
        ),
    },
    {
      title: "Stan roz. administracja",
      dataIndex: "administration_billing_status",
      sortable: false,
      searchable: true,
      search_method: "in",
      search_component: "select",
      options: administration_billing_status_filter_options,
      render: (_, { id, administration_billing_status }) =>
        renderLink(
          id,
          "administration_billing_status",
          getAdministratorBillingStatus(administration_billing_status)
        ),
    },
    {
      title: "Rodzaj",
      dataIndex: "issue_kind_id",
      sortable: true,
      searchable: true,
      search_method: "in",
      search_component: "select",
      options: issue_kind_options,
      width: 150,
      render: (_, { id, issue_kind }) =>
        renderLink(id, "issue_kind_id", issue_kind?.name),
    },
    {
      title: "Telefon do osoby w mieszkaniu",
      dataIndex: "client_contact_person_phone",
      sortable: true,
      searchable: true,
      search_method: "matches",
      search_type: "number",
      width: 170,
      render: (_, { id, client_contact_person_phone }) =>
        renderLink(
          id,
          "client_contact_person_phone",
          <span className="text-nowrap">
            {formatPhone(client_contact_person_phone)}
          </span>
        ),
    },
    {
      title: "Typ zgłoszenia",
      dataIndex: "issue_type",
      sortable: false,
      searchable: true,
      search_method: "in",
      search_component: "multi_select",
      options: issue_type_options,
      is_important: true,
      selectTagRender,
      render: (_, { issue_type }) => {
        const type = getIssueType(issue_type);
        return <Tag color={type.color}>{type.label}</Tag>;
      },
    },
  ];
};

export default getColumns;
